/**
 * Wire types for the card hand on the AOTD page: offers, the hand of 4, the
 * play window and the keep-4 reset. Mirror of the backend's
 * `n450s_backend/modules/cards/services/handTypes.ts` (plus the
 * `card.granted` metadata from `grantCard.ts`); the prose contract is
 * `n450s_backend/modules/aotd/quiz/CONTRACT.md`. Change both together.
 *
 * Paths are relative to `API_BASE_URL`; the card router answers on both
 * `/cards/...` and `/v2/respect/cards/...`.
 *
 * Read by the AOTD hand (`hand/AotdHand.tsx`) through `respectCardsApi`.
 */

import type { CardDefinition, CardOrigin, CardRarity, CardSource } from "./cardTypes";

/** Cards a player may hold in hand at once (every in_hand copy counts). */
export const HAND_SIZE = 4;
/** Cards may be played from the day's unlock to unlock + 45 min. */
export const PLAY_WINDOW_MS = 45 * 60 * 1000;
/** Reclamation: the most used emojis one play reclaims. */
export const RECLAIM_MAX_EMOJIS = 10;

export type CardGrantVisibility = "public" | "private";

/** A card waiting on a keep-or-throw (a CardInstance with status "offered"). */
export interface CardOffer {
  /** The offered instance id. */
  id: string;
  cardId: string;
  definition: CardDefinition | null;
  source: CardSource;
  visibility: CardGrantVisibility;
  /** ISO. Offers queue oldest first; show only the first. */
  offeredAt: string;
  /** Short reason for the UI, e.g. "you won today's emoji quiz". */
  reason: string | null;
  /** Where the card came from ("From <label>"). Optional for an older backend. */
  origin?: CardOrigin | null;
}

export interface HandInfo {
  /** Always HAND_SIZE (4). */
  size: number;
  /** Every in_hand copy (copies of one card count separately). */
  inHandCount: number;
  /** inHandCount > size: the keep-4 picker must run before anything else. */
  overLimit: boolean;
}

export interface PlayWindow {
  /** Inside today's unlock to unlock + 45 min at the server's now. */
  open: boolean;
  /** Today's window (ISO), or null when today's unlock is unknown. */
  opensAt: string | null;
  closesAt: string | null;
  /** The next window's start when the current one is shut (ISO), else null. */
  nextOpensAt: string | null;
}

/** Added to GET /cards/my-cards next to `cards` and `tiles`. */
export interface MyCardsHandAdditions {
  offers: CardOffer[];
  hand: HandInfo;
  playWindow: PlayWindow;
}

// POST /cards/offers/:id/resolve

/**
 * Keep the offered card (throwing `discardInstanceId`, one of your in_hand
 * cards, when the hand is full) or throw the offered card away.
 * `discardInstanceId` may be omitted only when the hand has room.
 */
export type ResolveOfferRequest =
  | { keep: true; discardInstanceId?: string }
  | { keep: false };

export interface ResolveOfferResponse {
  success: true;
  kept: boolean;
  /** The offered instance (now in_hand or discarded). */
  instanceId: string;
  /** The in_hand card thrown to make room, now discarded. */
  discardedInstanceId: string | null;
  /** The next queued offer, if any. */
  nextOffer: CardOffer | null;
}

export type ResolveOfferErrorCode =
  | "offer_not_found"
  | "offer_not_pending"
  | "discard_required"
  | "discard_not_in_hand"
  | "hand_over_limit";

// POST /cards/keep

/** The keep-4 picker: exactly `hand.size` of your in_hand instance ids. */
export interface KeepCardsRequest {
  instanceIds: string[];
}

export interface KeepCardsResponse {
  success: true;
  kept: string[];
  /** Now returned (given back to the house). */
  returned: string[];
}

export type KeepCardsErrorCode = "not_over_limit" | "bad_selection" | "not_in_hand";

export interface CardHandErrorResponse<C extends string = string> {
  success: false;
  code: C;
  message: string;
}

/**
 * New `Playability.code` values (state "blocked"). `outside_play_window`
 * carries `until`, the next window's start. `hand_over_limit`: more than
 * HAND_SIZE cards in hand, so the keep-4 picker runs before any play.
 */
export type HandPlayabilityCode =
  | "outside_play_window"
  | "quiz_not_open"
  | "not_quiz_player"
  | "second_guess_used"
  | "no_used_emojis"
  | "hand_over_limit";

/**
 * New `errorCode` values from POST /cards/play/:id. `play_in_progress`: the
 * same card is already being played (a double tap or a retry); refetch, and
 * try again if it is still in hand.
 */
export type HandPlayErrorCode =
  | HandPlayabilityCode
  | "selection_required"
  | "selection_too_many"
  | "emoji_not_used"
  | "play_in_progress";

/** `card.granted` event metadata (what the sitebot and siteline see). */
export interface CardGrantedEventMetadata {
  instanceId: string;
  userId: string;
  username: string | null;
  cardSlug: string;
  cardName: string;
  cardEmoji: string;
  rarity: CardRarity;
  source: CardSource;
  visibility: CardGrantVisibility;
  status: "in_hand" | "offered";
  grantKey: string | null;
  reason: string | null;
  origin?: CardOrigin;
}

/** Route paths relative to `API_BASE_URL`, in the style of `respectCardsPlayerPaths`. */
export const handPaths = {
  resolveOffer: (instanceId: string) => `/cards/offers/${encodeURIComponent(instanceId)}/resolve`,
  keep: "/cards/keep",
} as const;
